import BigNumber from 'bignumber.js';

import { PerpetualMarket } from '#/api/markets';
import { MarketSummary } from '#/api/markets-summary';

import { logException } from '#/features/logging/logging';

export type MarginCheckType = 'Initial' | 'Maintenance';
export type Side = 'BUY' | 'SELL';

/**
 * Calculates the margin requirement for a perpetual option
 * @see https://www.notion.so/tradeparadigm/Cross-margined-Options-b61ca41ad25a427db69a4392abbed6c7
 */
export function calculateOptionMarginRequirement(
  checkType: MarginCheckType,
  market: PerpetualMarket,
  marketSummary: MarketSummary,
  side: Side,
): BigNumber {
  if (marketSummary.mark_price == null) {
    logException(
      `calculateOptionMarginRequirement: optionMarkPrice is null for market='${market.symbol}'`,
    );
    return BigNumber(0);
  }
  if (marketSummary.underlying_price == null) {
    logException(
      `calculateOptionMarginRequirement: spotPrice is null for market='${market.symbol}'`,
    );
    return BigNumber(0);
  }
  if (market.strike_price == null) {
    logException(
      `calculateOptionMarginRequirement: strikePrice is null for market='${market.symbol}'`,
    );
    return BigNumber(0);
  }

  if (market.option_type == null) {
    logException(
      `calculateOptionMarginRequirement: optionType is null for market='${market.symbol}'`,
    );
    return BigNumber(0);
  }

  if (market.option_cross_margin_params == null) {
    logException(
      `calculateOptionMarginRequirement: optionCrossMarginParams is null for market='${market.symbol}'`,
    );
    return BigNumber(0);
  }

  let margin: BigNumber;
  const params =
    checkType === 'Initial'
      ? market.option_cross_margin_params.imf
      : market.option_cross_margin_params.mmf;

  if (side === 'BUY') {
    margin = BigNumber.min(
      params.premium_multiplier.times(marketSummary.mark_price),
      params.long_itm.times(marketSummary.underlying_price),
    );
  } else {
    // Calculate OTM amount based on option type
    let otmAmount = market.strike_price.minus(marketSummary.underlying_price);
    if (market.option_type === 'PUT') {
      otmAmount = otmAmount.negated();
    }

    otmAmount = BigNumber.max(0, otmAmount);

    const itm = params.short_itm
      .times(marketSummary.underlying_price)
      .minus(otmAmount);
    const otm = params.short_otm.times(marketSummary.underlying_price);
    margin = BigNumber.max(itm, otm);

    // Cap short put options
    if (market.option_type === 'PUT') {
      const cap = params.short_put_cap.times(market.strike_price);
      margin = BigNumber.min(margin, cap);
    }
  }

  return margin;
}
